import { gql } from "@apollo/client";
import { guardNever } from "@hex/common";
import { keyBy, sortBy } from "lodash";
import React, { useMemo } from "react";

import { ORG_ID } from "../../orgs.js";
import {
  usePinnedSchemasQuery,
  usePinnedSemanticDatasetsQuery,
  usePinnedSemanticViewsQuery,
  usePinnedTablesQuery,
} from "../sidebar/data-browser/FavoritesView.generated.js";

import { FavoriteDataSchemaRow } from "./favorite-projects-and-collection-rows/FavoriteDataSchemaRow.js";
import { FavoriteDataSemanticResourceRow } from "./favorite-projects-and-collection-rows/FavoriteDataSemanticResourceRow.js";
import { FavoriteDataTableRow } from "./favorite-projects-and-collection-rows/FavoriteDataTableRow.js";
import { SidebarSection } from "./favorite-projects-and-collection-rows/SidebarSection.js";
import { useGetDataConnectionsForHomePageSidebarQuery } from "./FavoriteData.generated.js";

gql`
  query GetDataConnectionsForHomePageSidebar($orgId: OrgId!) {
    orgDataConnections(orgId: $orgId, importable: true) {
      id
      connectionType
      isMultiDatabase
    }
  }
`;

type FavoriteDataProps = {
  hideIfEmpty?: boolean;
  location: "popover_sidebar" | "home_sidebar";
};
export const FavoriteData: React.ComponentType<FavoriteDataProps> = React.memo(
  function FavoriteData({ hideIfEmpty, location }: FavoriteDataProps) {
    // Our favorite/unfavorite mutations update the cache for
    // these queries we need to use the same queries here so that this data stays up to
    // date.
    const {
      data: schemasData,
      error: schemasError,
      loading: schemasLoading,
    } = usePinnedSchemasQuery({ variables: { hexId: null } });

    const {
      data: tablesData,
      error: tablesError,
      loading: tablesLoading,
    } = usePinnedTablesQuery({ variables: { hexId: null } });

    const {
      data: semanticDatasetsData,
      error: semanticDatasetsError,
      loading: semanticDatasetsLoading,
    } = usePinnedSemanticDatasetsQuery();

    const {
      data: semanticViewsData,
      error: semanticViewsError,
      loading: semanticViewsLoading,
    } = usePinnedSemanticViewsQuery();

    const { data, loading } = useGetDataConnectionsForHomePageSidebarQuery({
      variables: { orgId: ORG_ID },
    });

    const connectionMap = useMemo(
      () => keyBy(data?.orgDataConnections, "id"),
      [data],
    );

    const pinnedItems = useMemo(() => {
      const items = [
        ...(tablesData?.me?.pinnedTables ?? []),
        ...(semanticDatasetsData?.me?.pinnedSemanticDatasets ?? []),
        ...(semanticViewsData?.me?.pinnedSemanticViews ?? []),
      ];

      return sortBy(items, (i) => i.pinnedAt).reverse();
    }, [
      tablesData?.me?.pinnedTables,
      semanticDatasetsData?.me?.pinnedSemanticDatasets,
      semanticViewsData?.me?.pinnedSemanticViews,
    ]);

    const favoriteDataRows = useMemo(() => {
      if (loading || data == null) {
        return [];
      }

      const rows = [];

      for (const schema of schemasData?.me?.pinnedSchemas ?? []) {
        const connection = connectionMap[schema.dataConnectionId];
        if (connection != null) {
          const multiDatabase = connection.isMultiDatabase;

          rows.push(
            <FavoriteDataSchemaRow
              key={schema.id}
              multiDatabase={multiDatabase ?? false}
              schema={schema}
            />,
          );
        }
      }

      for (const pinnedItem of pinnedItems) {
        if (pinnedItem.__typename === "DataSourceTable") {
          const table = pinnedItem;
          const connection = connectionMap[table.dataConnectionId];
          if (connection != null) {
            const multiDatabase = connection.isMultiDatabase;
            rows.push(
              <FavoriteDataTableRow
                key={table.id}
                connectionType={connection.connectionType}
                counterEventName={`${location}.favorites.data.click`}
                dataConnectionId={connection.id}
                multiDatabase={multiDatabase ?? false}
                table={table}
              />,
            );
          }
        } else if (
          pinnedItem.__typename === "SemanticDataset" ||
          pinnedItem.__typename === "SemanticView"
        ) {
          const semanticResource = pinnedItem;
          rows.push(
            <FavoriteDataSemanticResourceRow
              key={semanticResource.id}
              semanticResource={semanticResource}
            />,
          );
        } else {
          guardNever(pinnedItem, pinnedItem);
        }
      }

      return rows;
    }, [
      connectionMap,
      data,
      loading,
      location,
      schemasData?.me?.pinnedSchemas,
      pinnedItems,
    ]);

    return (
      <SidebarSection
        error={
          tablesError ??
          schemasError ??
          semanticDatasetsError ??
          semanticViewsError
        }
        hideIfEmpty={hideIfEmpty}
        items={favoriteDataRows}
        loading={
          schemasLoading ||
          tablesLoading ||
          semanticDatasetsLoading ||
          semanticViewsLoading ||
          loading
        }
        localStorageKey="SIDEBAR_FAVORITE_DATA_OPENED"
        text="Favorite data"
      />
    );
  },
);
